import React, { useMemo, useRef, useState } from 'react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, ScatterplotLayer, PolygonLayer, IconLayer, TextLayer } from '@deck.gl/layers';
import { MVTLayer } from '@deck.gl/geo-layers';
import { PathStyleExtension, CollisionFilterExtension } from '@deck.gl/extensions';
import { logger } from '../../utils/logger';
import { API_ENDPOINTS } from '../../config/chargerApiConfig';
import { extractPolygonCoordinateSets } from '../../utils/geoJsonUtils';
import { CIRCUIT_CAPACITY_BUCKETS, getCircuitColorHex, parseLadwpCapacityValue } from './circuitColorUtils';

const buildTileUrl = (baseUrl, filters = {}, searchId = null) => {
    if (!baseUrl) return baseUrl;
    const params = new URLSearchParams();

    if (searchId) {
        params.set('search_id', searchId);
    } else {
        const allowedKeys = [
            'value_min',
            'value_max',
            'area_min',
            'area_max',
            'zip',
            'city',
            'utility_region_acronym',
            'near_dc',
            'near_scaqmd',
            'min_score',
            'top'
        ];

        allowedKeys.forEach((key) => {
            const value = filters?.[key];
            if (value !== undefined && value !== null && value !== '') {
                params.set(key, value);
            }
        });
    }

    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
};

const isTruthyFlag = (value) => {
    if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return value === true || value === 1;
};

const FEATURE_ID_KEYS = [
    'apn',
    'APN',
    'parcel_apn',
    'ParcelAPN',
    'parcelId',
    'parcel_id',
    'ParcelID',
    'Parcel_Id',
    'id',
    'ID'
];

const getParcelFeatureIdentifier = (properties = {}) => {
    if (!properties || typeof properties !== 'object') return null;

    for (const key of FEATURE_ID_KEYS) {
        const value = properties[key];
        if (value !== undefined && value !== null && value !== '') {
            return String(value).toLowerCase();
        }
    }

    return null;
};

const isTopParcelFeature = (props = {}) => {
    return isTruthyFlag(
        props.is_top ??
        props.top ??
        props.top_parcel ??
        props.isTop ??
        props.Is_Top
    );
};

// Backend no longer clusters; keep helpers minimal for safety
const isClusterFeature = () => false;

const getPolygonCenter = (coordinates) => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
    const ring = coordinates[0];
    if (!Array.isArray(ring) || ring.length === 0) return null;
    let sumLng = 0;
    let sumLat = 0;
    let count = 0;
    ring.forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        const lng = Number(point[0]);
        const lat = Number(point[1]);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        sumLng += lng;
        sumLat += lat;
        count += 1;
    });
    if (count === 0) return null;
    return [sumLng / count, sumLat / count];
};

const DeckGLOverlay = ({
    map,
    // Data props
    scaqmdJurisdiction = [],
    sraData = [],
    mhdInfrastructure = [],
    warehouses = [],
    disadvantagedCommunities = [],
    calEnviroScreen = [],
    truckStops = [],
    circuitData = [],
    circuitContours = [],
    // heatmapData removed - now using MVT tiles directly
    trafficData = [],
    utilitiesData = [],
    // Note: landParcelData removed - now using MVT tiles directly
    // Checkbox states
    SCAQMDCheckbox = false,
    sraCheckbox = false,
    mhdInfrastructureCheckbox = false,
    warehouseCheckbox = false,
    utilitiesCheckbox = false,  
    disadvantagedCommunitiesCheckbox = false,
    calEnviroScreenCheckbox = false,
    truckStopCheckbox = false,
    intermodalTerminalCheckbox = false,
    serviceCenterCheckbox = false,
    circuitCapacityCheckbox = false,
    truckParkingLocationCheckbox = false,
    pemsTrafficCheckbox = false,
    landParcelCheckbox = false,
    // Event handlers
    onMhdMarkerClick,
    onTruckStopClick,
    onTrafficClick,
    onCircuitClick,
    onWarehouseClick,
    onUtilityClick,
    onCalEnviroScreenClick,
    onSraClick,
    onParcelClick,
    highlightedParcelId = null,
    currentZoom = 10,
    parcelFilters = {},
    topParcelPoints = [],
    searchId = null,
    parcelPointTileUrl: parcelPointTileUrlOverride = null,
    parcelPolygonTileUrl: parcelPolygonTileUrlOverride = null
}) => {
    
    const parcelIconCacheRef = useRef(new Map());
    const [hoveredParcelId, setHoveredParcelId] = useState(null);

    const parcelPolygonTileUrl = useMemo(
        () => parcelPolygonTileUrlOverride || buildTileUrl(API_ENDPOINTS.vectorTiles.parcels, parcelFilters, searchId),
        [parcelFilters, searchId, parcelPolygonTileUrlOverride]
    );

    const parcelPointTileUrl = useMemo(
        () => parcelPointTileUrlOverride || buildTileUrl(API_ENDPOINTS.vectorTiles.parcelPoints, parcelFilters, searchId),
        [parcelFilters, searchId, parcelPointTileUrlOverride]
    );

    const topParcelPillData = useMemo(() => {
        if (!Array.isArray(topParcelPoints) || topParcelPoints.length === 0) {
            return [];
        }

        return topParcelPoints
            .map(point => {
                if (!Number.isFinite(point?.lon) || !Number.isFinite(point?.lat)) {
                    return null;
                }
                const parcelId = point.id ? String(point.id).toLowerCase() : `${point.lon}-${point.lat}`;
                return {
                    id: `top-parcel-${parcelId}`,
                    clusterId: null,
                    position: [point.lon, point.lat],
                    isCluster: false,
                    count: 1,
                    landValue: point.landValue ?? point.land_value ?? 0,
                    parcelId,
                    rawParcel: point.parcel ?? point.rawParcel ?? null
                };
            })
            .filter(Boolean);
    }, [topParcelPoints]);

    const topParcelPillLayer = useMemo(() => {
        if (!topParcelPillData.length) return [];

        logger.log('Top parcel pill data length:', topParcelPillData.length, {
            sample: topParcelPillData.slice(0, 3)
        });

        return [
            new IconLayer({
                id: 'top-parcel-pills-layer',
                data: topParcelPillData,
                pickable: true,
                sizeUnits: 'pixels',
                sizeScale: 1,
                getSize: () => 1,
                billboard: true,
                opacity: 1,
                parameters: {
                    depthTest: false
                },
                getIcon: d => getParcelIconTexture(
                    parcelIconCacheRef,
                    d.landValue > 0 ? formatParcelDisplayValue(d.landValue) : '$0',
                    {
                        isCluster: false,
                        highlighted: highlightedParcelId && d.parcelId === highlightedParcelId
                    }
                ),
                getPosition: d => d.position,
                onClick: info => {
                    if (!info.object) return;
                    if (info.object.rawParcel && onParcelClick) {
                        onParcelClick(info.object.rawParcel, {
                            latLng: {
                                lat: () => info.object.position[1],
                                lng: () => info.object.position[0]
                            }
                        });
                    }
                }
            })
        ];
    }, [topParcelPillData, highlightedParcelId, map, onParcelClick, currentZoom]);

    // Process SCAQMD jurisdiction data
    const scaqmdLayers = useMemo(() => {
        try {

            if (!SCAQMDCheckbox || !Array.isArray(scaqmdJurisdiction) || scaqmdJurisdiction.length === 0) {
                return [];
            }

            const processedJurisdictions = scaqmdJurisdiction
                .map(jurisdiction => {
                    const geojsonGeometry = jurisdiction.geometry;
                    if (!geojsonGeometry || !geojsonGeometry.coordinates) return null;

                    // GeoJSON coordinates are already in the correct format for deck.gl
                    return {
                        ...jurisdiction,
                        coordinates: geojsonGeometry.coordinates
                    };
                })
                .filter(Boolean);

            if (processedJurisdictions.length === 0) return [];

            return [
                new PolygonLayer({
                    id: 'scaqmd-layer',
                    data: processedJurisdictions,
                    pickable: true,
                    filled: true,
                    stroked: true,
                    getPolygon: d => d.coordinates,
                    getFillColor: [147, 51, 234, 80], // Purple, semi-transparent  
                    getLineColor: [147, 51, 234, 255], // Purple border
                    getLineWidth: 2,
                    onClick: (info) => {
                        logger.log('SCAQMD jurisdiction clicked:', info.object);
                    }
                })
            ];
        } catch (error) {
            logger.error('Error creating SCAQMD layers:', error);
            return []; // Return empty array if there's an error
        }
    }, [SCAQMDCheckbox, scaqmdJurisdiction]);

    const sraLayers = useMemo(() => {
        if (!sraCheckbox || !Array.isArray(sraData) || sraData.length === 0) {
            return [];
        }

        const processed = [];
        sraData.forEach((feature, index) => {
            const geom = feature?.geometry;
            if (!geom?.coordinates) return;

            if (geom.type === 'Polygon') {
                processed.push({
                    ...feature,
                    coordinates: geom.coordinates
                });
            } else if (geom.type === 'MultiPolygon') {
                geom.coordinates.forEach((polygonCoords, polyIndex) => {
                    processed.push({
                        ...feature,
                        id: `${feature.id || index}_${polyIndex}`,
                        coordinates: polygonCoords
                    });
                });
            }
        });

        if (processed.length === 0) return [];
        const textData = processed
            .map((item) => {
                const position = getPolygonCenter(item.coordinates);
                if (!position) return null;
                return {
                    ...item,
                    position,
                    label: String(item?.properties?.monitoring ?? item?.monitoring ?? '')
                };
            })
            .filter((item) => item && item.label);

        return [
            new PolygonLayer({
                id: 'sra-layer',
                data: processed,
                pickable: true,
                filled: true,
                stroked: true,
                getPolygon: d => d.coordinates,
                getFillColor: [14, 165, 233, 55],
                getLineColor: [8, 145, 178, 255],
                getLineWidth: 3,
                lineWidthMinPixels: 2,
                opacity: 0.5,
                onClick: (info) => {
                    logger.log('SRA clicked:', info.object);
                    if (onSraClick && info.object) {
                        onSraClick(info.object, info.coordinate);
                    }
                }
            }),
            new TextLayer({
                id: 'sra-text-layer',
                data: textData,
                pickable: true,
                getPosition: d => d.position,
                getText: d => d.label,
                getSize: 12,
                sizeUnits: 'pixels',
                getColor: [0, 67, 99, 255],
                getTextAnchor: 'middle',
                getAlignmentBaseline: 'center',
                getBackgroundColor: [255, 255, 255, 210],
                background: true
            })
        ];
    }, [sraCheckbox, sraData, onSraClick]);

    const warehouseLayers = useMemo(() => {
        if (!warehouseCheckbox || !Array.isArray(warehouses) || warehouses.length === 0) {
            return [];
        }

        const processedWarehouses = warehouses
            .flatMap((warehouse, index) => {
                const polygons = extractPolygonCoordinateSets(warehouse.geometry);
                if (polygons.length === 0) {
                    logger.warn(`Warehouse ${warehouse.id ?? index} skipped: unsupported geometry`);
                    return [];
                }

                return polygons.map((coordinates, polygonIndex) => ({
                    ...warehouse,
                    id: polygons.length > 1
                        ? `${warehouse.id ?? index}_${polygonIndex}`
                        : (warehouse.id ?? index),
                    coordinates // deck.gl _normalize will handle winding/closure
                }));
            })
            .filter(Boolean);

        if (processedWarehouses.length === 0) {
            logger.log("Processed warehouses array is empty");
            return [];
        }

        logger.log(`Processed ${processedWarehouses.length} warehouses`);
        logger.log("Sample warehouse coordinates:", processedWarehouses[0].coordinates);

        return [
            new PolygonLayer({
                id: "warehouses-layer",
                data: processedWarehouses,
                pickable: true,
                filled: true,
                stroked: true,
                extruded: false,
                wireframe: false,
                lineWidthMinPixels: 1,
                getPolygon: d => d.coordinates,
                getFillColor: [5, 150, 105, 80],
                getLineColor: [5, 150, 105, 255],
                getLineWidth: 2,
                opacity: 0.5,
                _normalize: true, // fixes winding order and closes polygons
                parameters: {
                    depthTest: false
                },
                onClick: (info) => {
                    logger.log('Warehouse clicked:', info.object);
                    if (onWarehouseClick && info.object) {
                        onWarehouseClick(info.object, info.coordinate);
                    }
                }
            })
        ];
    }, [warehouseCheckbox, warehouses]);

    // Process disadvantaged communities data
    const disadvantagedLayers = useMemo(() => {
        if (!disadvantagedCommunitiesCheckbox || !Array.isArray(disadvantagedCommunities) || disadvantagedCommunities.length === 0) {
            return [];
        }

        logger.log("=== DISADVANTAGED COMMUNITIES DEBUG ===");
        logger.log("Communities count:", disadvantagedCommunities.length);
        logger.log("First community structure:", disadvantagedCommunities[0]);

        const processedCommunities = [];

        disadvantagedCommunities.forEach((community, index) => {
            const geom = community?.geometry;
            if (!geom?.coordinates) {
                logger.warn(`Community ${index} has no geometry`);
                return;
            }

            logger.log(`Community ${index} geometry type:`, geom.type);
            logger.log(`Community ${index} coordinates structure:`, {
                type: geom.type,
                coordinatesType: Array.isArray(geom.coordinates),
                coordinatesLength: geom.coordinates?.length,
                firstItemType: Array.isArray(geom.coordinates?.[0]),
                firstItemLength: geom.coordinates?.[0]?.length,
                firstPolygonFirstRing: geom.coordinates?.[0]?.[0]?.slice(0, 3),
                actualFirstCoordinate: geom.coordinates?.[0]?.[0]?.[0]
            });

            if (geom.type === 'Polygon') {
                // For Polygon: coordinates = [exteriorRing, hole1, hole2, ...]
                // Each ring should be [[lng, lat], [lng, lat], ...]
                processedCommunities.push({
                    ...community,
                    coordinates: geom.coordinates
                });
            } else if (geom.type === 'MultiPolygon') {
                // For MultiPolygon: coordinates = [polygon1, polygon2, ...]
                // Each polygon = [exteriorRing, hole1, hole2, ...]
                // Split MultiPolygon into separate polygons
                geom.coordinates.forEach((polygonCoords, polyIndex) => {
                    processedCommunities.push({
                        ...community,
                        id: `${community.id || index}_${polyIndex}`,
                        coordinates: polygonCoords
                    });
                });
            } else {
                logger.warn(`Unknown geometry type for community ${index}:`, geom.type);
            }
        });

        logger.log(`Processed ${processedCommunities.length} community polygons from ${disadvantagedCommunities.length} features`);
        
        if (processedCommunities.length > 0) {
            logger.log("Sample processed coordinates:", processedCommunities[0].coordinates);
        }

        if (processedCommunities.length === 0) return [];

        return [
            new PolygonLayer({
                id: 'disadvantaged-communities-layer',
                data: processedCommunities,
                pickable: true,
                filled: true,
                stroked: true,
                getPolygon: d => d.coordinates,
                getFillColor: [220, 38, 38, 80],
                getLineColor: [220, 38, 38, 255],
                getLineWidth: 2,
                opacity: 0.5,
                onClick: info => logger.log('Disadvantaged community clicked:', info.object)
            })
        ];
    }, [disadvantagedCommunitiesCheckbox, disadvantagedCommunities]);      

    const calEnviroScreenLayers = useMemo(() => {
        if (!calEnviroScreenCheckbox || !Array.isArray(calEnviroScreen) || calEnviroScreen.length === 0) {
            return [];
        }

        const getPercentile = (tract) => {
            const raw =
                tract?.properties?.ciscorep ??
                tract?.properties?.CIscoreP ??
                tract?.ciscorep ??
                tract?.CIscoreP;
            const parsed = Number(raw);
            if (!Number.isFinite(parsed)) return null;
            return Math.max(0, Math.min(100, parsed));
        };

        const getColorByPercentile = (percentile, alpha = 120) => {
            const p = Number.isFinite(percentile) ? percentile / 100 : 0;
            const red = Math.round(255 * p);
            const green = Math.round(255 * (1 - p));
            return [red, green, 40, alpha];
        };

        const processedTracts = [];
        calEnviroScreen.forEach((tract, index) => {
            const geom = tract?.geometry;
            if (!geom?.coordinates) return;

            if (geom.type === 'Polygon') {
                processedTracts.push({
                    ...tract,
                    percentile: getPercentile(tract),
                    coordinates: geom.coordinates
                });
            } else if (geom.type === 'MultiPolygon') {
                geom.coordinates.forEach((polygonCoords, polyIndex) => {
                    processedTracts.push({
                        ...tract,
                        id: `${tract.id || index}_${polyIndex}`,
                        percentile: getPercentile(tract),
                        coordinates: polygonCoords
                    });
                });
            }
        });

        if (processedTracts.length === 0) return [];

        return [
            new PolygonLayer({
                id: 'calenviroscreen-40-layer',
                data: processedTracts,
                pickable: true,
                filled: true,
                stroked: true,
                getPolygon: d => d.coordinates,
                getFillColor: d => getColorByPercentile(d.percentile, 145),
                getLineColor: d => getColorByPercentile(d.percentile, 255),
                getLineWidth: 2.5,
                lineWidthMinPixels: 2,
                lineWidthMaxPixels: 4,
                opacity: 0.9,
                onClick: (info) => {
                    logger.log('CalEnviroScreen tract clicked:', info.object);
                    if (onCalEnviroScreenClick && info.object) {
                        onCalEnviroScreenClick(info.object, info.coordinate);
                    }
                }
            })
        ];
    }, [calEnviroScreenCheckbox, calEnviroScreen, onCalEnviroScreenClick]);

    // Process circuit data for deck.gl
    const getCircuitColorRgb = (capacityValue, alpha = 255) => hexToRgb(getCircuitColorHex(capacityValue), alpha);
    
    // Vector Tile Circuit Layers (Combined SCE + LADWP)
    const circuitLayersCombined = useMemo(() => {
        const normalizedZoom = Math.ceil(currentZoom ?? 0);
        logger.log('Circuit layer check:', {
            circuitCapacityCheckbox,
            currentZoom,
            normalizedZoom,
            shouldLoad: circuitCapacityCheckbox && normalizedZoom >= 13
        });
        
        if (!circuitCapacityCheckbox) {
            logger.log('Circuit capacity checkbox is OFF');
            return [];
        }
                
        logger.log('Creating combined circuits MVT layer at zoom:', currentZoom, 'normalized:', normalizedZoom);
        logger.log('MVT URL:', API_ENDPOINTS.vectorTiles.circuitsCombined);
        
        return [
            new MVTLayer({
                id: 'circuits-combined-mvt',
                data: API_ENDPOINTS.vectorTiles.circuitsCombined,
                minZoom: 0,
                maxZoom: 22,
                pickable: true,
                autoHighlight: true,
                highlightColor: [255, 255, 255, 100],
                lineWidthMinPixels: normalizedZoom <= 14 ? 2 : 3,
                getLineColor: f => {
                    const utility = f.properties.utility || 'UNKNOWN';
                    
                    // Debug: Log ALL property keys for first few features to see what's available
                    if (Math.random() < 0.005) { // Log ~0.5% of features
                        logger.log('=== CIRCUIT FEATURE PROPERTIES DEBUG ===');
                        logger.log('All property keys:', Object.keys(f.properties));
                        logger.log('All property values:', Object.entries(f.properties));
                        logger.log('Utility:', utility);
                        logger.log('Capacity-related properties:', {
                            capacity: f.properties.capacity,
                            capacity_kw: f.properties.capacity_kw,
                            hosting_capacity: f.properties.hosting_capacity,
                            capacity_status: f.properties.capacity_status
                        });
                        logger.log('Name-related properties:', {
                            circuit_name: f.properties.circuit_name,
                            name: f.properties.name,
                            circuitName: f.properties.circuitName,
                            circuit_id: f.properties.circuit_id
                        });
                    }
                    
                    // Color-code by utility and capacity
                    if (utility === 'SCE') {
                        const capacity = f.properties.capacity ||
                            f.properties.capacity_kw ||
                            f.properties.hosting_capacity ||
                            0;
                        return getCircuitColorRgb(capacity);
                    } else if (utility === 'LADWP') {
                        const capacityValue = parseLadwpCapacityValue(f.properties);
                        return getCircuitColorRgb(capacityValue);
                    }
                    
                    // Fallback color for unknown utilities
                    return [153, 153, 153, 255]; // Gray
                },
                getLineWidth: normalizedZoom <= 14 ? 2 : 3,
                onClick: (info) => {
                    logger.log('=== CIRCUIT CLICK DEBUG ===');
                    logger.log('Full info object:', info);
                    logger.log('info.object:', info.object);
                    
                    if (info.object) {
                        const props = info.object.properties;
                        logger.log('Raw properties from vector tile:', props);
                        logger.log('All property keys:', Object.keys(props));
                        logger.log('Property values:', Object.entries(props));
                        
                        if (onCircuitClick) {
                            logger.log('Calling onCircuitClick with props:', props);
                            onCircuitClick(props, info.coordinate);
                        } else {
                            logger.warn('onCircuitClick handler is not defined');
                        }
                    } else {
                        logger.warn('info.object is null or undefined');
                    }
                },
                onDataLoad: (data) => {
                    logger.log('=== CIRCUIT MVT DATA LOADED ===');
                    logger.log('Tile data:', data);
                    if (data && data.length > 0) {
                        logger.log('First feature sample:', data[0]);
                        if (data[0].properties) {
                            logger.log('Sample properties:', data[0].properties);
                            logger.log('Sample property keys:', Object.keys(data[0].properties));
                        }
                    }
                },
                onError: (error) => {
                    logger.error('=== CIRCUIT MVT ERROR ===');
                    logger.error('Error details:', error);
                },
                updateTriggers: {
                    getLineColor: normalizedZoom,
                    getLineWidth: normalizedZoom
                }
            })
        ];
    }, [circuitCapacityCheckbox, currentZoom]);

    // Vector Tile Heatmap Hexagons (Truck Parking Heatmap)
    const heatmapHexagonLayers = useMemo(() => {
        if (!truckParkingLocationCheckbox) {
            return [];
        }
        
        logger.log('Creating heatmap hexagons MVT layer at zoom:', currentZoom);
        
        // Color scheme for heatmap weight
        const getHeatmapColor = (weight) => {
            if (weight > 1000) return [128, 0, 38, 180];      // #800026
            if (weight > 500) return [189, 0, 38, 180];       // #BD0026
            if (weight > 200) return [227, 26, 28, 180];      // #E31A1C
            if (weight > 100) return [252, 78, 42, 180];      // #FC4E2A
            if (weight > 50) return [253, 141, 60, 180];      // #FD8D3C
            if (weight > 20) return [254, 178, 76, 180];      // #FEB24C
            if (weight > 10) return [254, 217, 118, 180];     // #FED976
            return [255, 237, 160, 180];                       // #FFEDA0
        };
        
        return [
            new MVTLayer({
                id: 'heatmap-hexagons-mvt',
                data: API_ENDPOINTS.vectorTiles.heatmapHexagons,
                minZoom: 0,
                maxZoom: 22,
                pickable: true,
                filled: true,
                stroked: false,  // No borders for clean heatmap appearance
                getFillColor: f => {
                    const weight = f.properties?.weight ?? 0;
                    return getHeatmapColor(weight);
                },
                updateTriggers: {
                    getFillColor: [truckParkingLocationCheckbox]
                },
                onClick: (info) => {
                    if (info.object) {
                        const props = info.object.properties;
                        logger.log('Heatmap hexagon clicked:', {
                            weight: props.weight,
                            point_count: props.point_count,
                            hex_level: props.hex_level
                        });
                    }
                }
            })
        ];
    }, [truckParkingLocationCheckbox, currentZoom]);

    // Vector Tile Circuit Capacity Contours (displayed at low zoom levels 0-12)
    const circuitContourLayers = useMemo(() => {
        logger.log('Contour layer check:', {
            circuitCapacityCheckbox,
            currentZoom,
            shouldLoad: circuitCapacityCheckbox && currentZoom < 13
        });
        
        if (!circuitCapacityCheckbox || currentZoom >= 13) {
            logger.log('Contours not loading - checkbox:', circuitCapacityCheckbox, 'zoom:', currentZoom);
            return [];
        }
        
        logger.log('Creating circuit contours MVT layer at zoom:', currentZoom);
        
        return [
            new MVTLayer({
                id: 'circuit-contours-mvt',
                data: API_ENDPOINTS.vectorTiles.circuitContours,
                minZoom: 0,
                maxZoom: 12,
                pickable: true,
                filled: true,
                stroked: false,  // Disable borders to prevent overlapping contour artifacts
                getFillColor: f => {
                    const color = getColorFromContourProperties(
                    f.properties,
                    ['fill_color', 'fillColor', 'color', 'fill', 'color_hex', 'fill_hex'],
                    [79, 70, 229, 100]
                    );
                    // Increase opacity for better visibility without borders
                    return [color[0], color[1], color[2], 180];
                },
                // Border properties removed since stroked: false
                onClick: (info) => {
                    if (info.object) {
                        const props = info.object.properties;
                        logger.log('Circuit contour clicked:', {
                            capacity_range: `${props.capacity_kw_min}-${props.capacity_kw_max} kW`,
                            utility: props.utility,
                            area: props.area_sq_km || 'N/A'
                        });
                        if (onCircuitClick) {
                            onCircuitClick({ ...props, type: 'contour' });
                        }
                    }
                }
            })
        ];
    }, [circuitCapacityCheckbox, currentZoom]);

    // Vector Tile Land Parcel Layer (MVT - Server-side rendering)
    const parcelLayers = useMemo(() => {
        if (!landParcelCheckbox) {
            return [];
        }

        if (currentZoom < 14) {
            logger.log('Zoom too low for parcel polygons (< 14):', currentZoom);
            return [];
        }
        
        logger.log('Creating MVT parcel layer at zoom:', currentZoom);
        
        return [
            new MVTLayer({
                id: 'parcels-mvt',
                data: parcelPolygonTileUrl,
                minZoom: 0,
                maxZoom: 22,
                pickable: true,
                autoHighlight: true,
                highlightColor: [255, 215, 0, 180], // Gold highlight
                filled: true,
                stroked: true,
                getFillColor: f => {
                    // Properties are now lowercase from backend
                    const parcelId = f.properties.apn || f.properties.id;
                    if (highlightedParcelId && parcelId === highlightedParcelId) {
                        return [255, 215, 0, 100]; // Gold fill for selected
                    }
                    return [79, 70, 229, 40]; // Indigo-600 with low opacity
                },
                getLineColor: f => {
                    const parcelId = f.properties.apn || f.properties.id;
                    if (highlightedParcelId && parcelId === highlightedParcelId) {
                        return [255, 215, 0, 255]; // Gold border for selected
                    }
                    return [79, 70, 229, 180]; // Indigo-600 border
                },
                getLineWidth: f => {
                    const parcelId = f.properties.apn || f.properties.id;
                    return (highlightedParcelId && parcelId === highlightedParcelId) ? 3 : 1;
                },
                lineWidthMinPixels: 1,
                onClick: (info) => {
                    if (info.object && onParcelClick) {
                        const props = info.object.properties;
                        logger.log('Parcel clicked:', {
                            apn: props.apn,
                            address: props.address,
                            land_value: props.land_value
                        });
                        onParcelClick(props, info.coordinate);
                    }
                },
                updateTriggers: {
                    getFillColor: [highlightedParcelId],
                    getLineColor: [highlightedParcelId],
                    getLineWidth: [highlightedParcelId]
                }
            })
        ];
    }, [landParcelCheckbox, currentZoom, highlightedParcelId, onParcelClick]);

    // Parcel Points Layer (MVT points)
    // Shows markers with land values at all zoom levels (no server-side clustering)
    const parcelPointsLayers = useMemo(() => {
        if (!landParcelCheckbox) {
            logger.log('Parcel points layer disabled (checkbox off)');
            return [];
        }
        
        logger.log('=== CREATING PARCEL POINTS MVT LAYER ===');
        logger.log('Zoom:', currentZoom);
        logger.log('MVT URL:', parcelPointTileUrl);
        
        return [
            new MVTLayer({
                id: 'parcel-points-mvt',
                data: parcelPointTileUrl,
                minZoom: 0,
                maxZoom: 22,
                pointType: 'circle',
                pickable: true,
                getFillColor: f => {
                    const props = f.properties;
                    const isCluster = isClusterFeature(props);
                    const id = props?.id || props?.apn;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === String(id).toLowerCase();
                    if (isCluster) return isHovered ? [255, 140, 0, 255] : [255, 255, 255, 255];
                    return [255, 255, 255, 255]; // Purple for all parcels; labels handled via TextLayer
                },

                getLineColor: f => {
                    const props = f.properties;
                    const isCluster = isClusterFeature(props);
                    const id = props?.id || props?.apn;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === String(id).toLowerCase();
                    if (isCluster) return isHovered ? [255, 140, 0, 255] : [79, 70, 229, 255];
                    return [94, 48, 210, 255];
                },

                getPointRadius: f => {
                    const props = f.properties;
                    const isCluster = isClusterFeature(props);
                    const id = props?.id || props?.apn;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === String(id).toLowerCase();
                    if (isCluster) return 8;
                    if (isHovered) return 12;
                    return 5.5;
                },
                pointRadiusUnits: 'pixels',
                pointRadiusMinPixels: 4,
                pointRadiusMaxPixels: 20,
                lineWidthUnits: 'pixels',
                lineWidthMinPixels: 2,

                onClick: (info) => {
                    logger.log('=== PARCEL POINT CLICKED ===');
                    logger.log('Info:', info);
                    
                    if (!info.object) return;
                    
                    const props = info.object.properties;
                    const isCluster = props.is_cluster === true || props.is_cluster === 1;
                    
                    logger.log('Properties:', props);
                    logger.log('Is cluster:', isCluster);
                    
                    if (isCluster) {
                        // Cluster clicked: zoom in
                        const coords = info.coordinate;
                        const nextZoom = Math.min(Math.ceil(currentZoom + 2), 22);
                        
                        logger.log('Zooming to:', nextZoom, 'at', coords);
                        
                        if (map?.panTo && coords) {
                            map.panTo({ lat: coords[1], lng: coords[0] });
                        }
                        if (map?.setZoom) {
                            map.setZoom(nextZoom);
                        }
                    } else {
                        // Individual parcel clicked
                        if (onParcelClick) {
                            logger.log('Calling onParcelClick with:', props);
                            onParcelClick(props, info.coordinate);
                        }
                    }
                },
                onHover: (info) => {
                    const props = info.object?.properties;
                    const id = props?.id || props?.apn;
                    setHoveredParcelId(id ? String(id).toLowerCase() : null);
                },
                
                extensions: [new CollisionFilterExtension()],

                collisionEnabled: true,

                updateTriggers: {
                    getFillColor: [hoveredParcelId],
                    getLineColor: [hoveredParcelId],
                    getPointRadius: [hoveredParcelId],
                },
                transitions: {
                    getPointRadius: {
                        duration: 200,
                        easing: t => t * t * (3 - 2 * t)
                    }
                }
            })
        ];
    }, [landParcelCheckbox, currentZoom, hoveredParcelId, onParcelClick, map, parcelPointTileUrl]);

    // Price label layer for top parcels (use existing topParcelPoints prop)
    const topParcelPriceLabels = useMemo(() => {
        if (!landParcelCheckbox || !Array.isArray(topParcelPoints) || topParcelPoints.length === 0) {
            return [];
        }

        const formatPrice = (value) => {
            if (!Number.isFinite(value)) return '';
            if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2).replace(/\.0+$/, '')}M`;
            if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
            return `${Math.round(value)}`;
        };

        return [
            new TextLayer({
                id: 'parcel-price-labels',
                data: topParcelPoints,
                pickable: true,
                getPosition: d => [d.lon, d.lat],
                getText: d => `$${formatPrice(d.landValue ?? d.land_value)}`,
        
                // Smoothly animate between 14 → 18 px on hover
                getSize: d => {
                    const id = d.id ? String(d.id).toLowerCase() : null;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === id;
                    return isHovered ? 18 : 14;
                },
        
                // Fade smoothly (Deck.gl interpolates all 4 channels)
                getColor: d => {
                    const id = d.id ? String(d.id).toLowerCase() : null;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === id;
                    return isHovered ? [255, 255, 255, 255] : [255, 255, 255, 255];
                },
        
                // Smooth pill expansion color fade
                getBackgroundColor: d => {
                    const id = d.id ? String(d.id).toLowerCase() : null;
                    const isHovered = hoveredParcelId && id && hoveredParcelId === id;
                    return isHovered
                        ? [139, 92, 246, 255]  // brighter purple
                        : [126, 58, 242, 255]; // dimmer purple
                },
                
                // Pill styling
                background: true,
                backgroundPadding: [8, 4],
                backgroundBorderRadius: 48,
                billboard: true,
                getTextAnchor: 'middle',
                getAlignmentBaseline: 'center',
        
                // Font styling
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 800,

                // Hover logic
                onHover: info => {
                    const id = info.object?.id;
                    setHoveredParcelId(id ? String(id).toLowerCase() : null);
                },
        
                // Click logic
                onClick: info => {
                    const rawParcel = info.object?.parcel || info.object?.rawParcel || null;
                    if (onParcelClick && rawParcel) {
                        onParcelClick(rawParcel, info.coordinate);
                    }
                },
        
                transitions: {
                    getSize: {
                        duration: 150,
                        easing: t => t * t * (3 - 2 * t) // smoothstep
                    },
                    getColor: {
                        duration: 150,
                        easing: t => t * t * (3 - 2 * t)
                    },
                    getBackgroundColor: {
                        duration: 150,
                        easing: t => t * t * (3 - 2 * t)
                    }
                },

                extensions: [new CollisionFilterExtension()],
        
                updateTriggers: {
                    getSize: [hoveredParcelId],
                    getColor: [hoveredParcelId],
                    getBackgroundColor: [hoveredParcelId]
                }
            })
        ];
    }, [landParcelCheckbox, topParcelPoints, hoveredParcelId, onParcelClick]);

    // Process MHD infrastructure markers
    const mhdLayers = useMemo(() => {
        if (!mhdInfrastructureCheckbox || !Array.isArray(mhdInfrastructure) || mhdInfrastructure.length === 0) {
            return [];
        }

        const processedInfrastructure = mhdInfrastructure.map(infrastructure => {
            const lat = parseFloat(infrastructure.lat);
            const lon = parseFloat(infrastructure.lon);

            if (isNaN(lat) || isNaN(lon)) {
                return null;
            }

            return {
                ...infrastructure,
                position: [lon, lat]
            };
        }).filter(Boolean);

        if (processedInfrastructure.length === 0) return [];

        return [
            new ScatterplotLayer({
                id: 'mhd-infrastructure-layer',
                data: processedInfrastructure,
                pickable: true,
                opacity: 0.8,
                stroked: true,
                filled: true,
                radiusScale: 1,
                radiusMinPixels: 8,
                radiusMaxPixels: 12,
                lineWidthMinPixels: 2,
                getPosition: d => d.position,
                getRadius: 12,
                getFillColor: [5, 150, 105], // Green
                getLineColor: [6, 95, 70], // Darker green
                onClick: (info, event) => {
                    if (info.object && onMhdMarkerClick) {
                        onMhdMarkerClick(info.object, event);
                    }
                }
            })
        ];
    }, [mhdInfrastructureCheckbox, mhdInfrastructure, onMhdMarkerClick]);

    // Process truck stops
    const truckStopLayers = useMemo(() => {
        if (!Array.isArray(truckStops) || truckStops.length === 0) {
            return [];
        }
        logger.log("Truck stop data: ", truckStops)

        // Separate truck stops by type
        const intermodalTerminals = truckStops.filter(stop => stop.type === 'intermodal_terminal');
        const serviceCenters = truckStops.filter(stop => stop.type === 'service_center');

        // Process intermodal terminals
        const processedIntermodalTerminals = intermodalTerminals.map(stop => {
            const lat = parseFloat(stop.latitude);
            const lng = parseFloat(stop.longitude);

            if (isNaN(lat) || isNaN(lng)) {
                return null;
            }

            return {
                ...stop,
                position: [lng, lat],
                color: [175, 110, 200] // Purple
            };
        }).filter(Boolean);

        // Process service centers
        const processedServiceCenters = serviceCenters.map(stop => {
            const lat = parseFloat(stop.latitude);
            const lng = parseFloat(stop.longitude);

            if (isNaN(lat) || isNaN(lng)) {
                return null;
            }

            return {
                ...stop,
                position: [lng, lat],
                color: [90, 170, 188] // Deeper blue
            };
        }).filter(Boolean);

        const layers = [];

        // Add intermodal terminals layer
        if (intermodalTerminalCheckbox && processedIntermodalTerminals.length > 0) {
            layers.push(
                new ScatterplotLayer({
                    id: 'intermodal-terminals-layer',
                    data: processedIntermodalTerminals,
                    pickable: true,
                    opacity: 0.8,
                    stroked: true,
                    filled: true,
                    radiusScale: 1,
                    radiusMinPixels: 8,
                    radiusMaxPixels: 12,
                    lineWidthMinPixels: 2,
                    getPosition: d => d.position,
                    getRadius: 12,
                    getFillColor: d => d.color,
                    getLineColor: [255, 255, 255], // White border
                    onClick: (info, event) => {
                        if (info.object && onTruckStopClick) {
                            onTruckStopClick(info.object, event);
                        }
                    }
                })
            );
        }

        // Add service centers layer
        if (serviceCenterCheckbox && processedServiceCenters.length > 0) {
            layers.push(
                new ScatterplotLayer({
                    id: 'service-centers-layer',
                    data: processedServiceCenters,
                    pickable: true,
                    opacity: 0.8,
                    stroked: true,
                    filled: true,
                    radiusScale: 1,
                    radiusMinPixels: 8,
                    radiusMaxPixels: 12,
                    lineWidthMinPixels: 2,
                    getPosition: d => d.position,
                    getRadius: 12,
                    getFillColor: d => d.color,
                    getLineColor: [255, 255, 255], // White border
                    onClick: (info, event) => {
                        if (info.object && onTruckStopClick) {
                            onTruckStopClick(info.object, event);
                        }
                    }
                })
            );
        }

        return layers;
    }, [intermodalTerminalCheckbox, serviceCenterCheckbox, truckStops, onTruckStopClick]);

    // Traffic color constants
    const TRAFFIC_COLORS = [
        "#006400", // dark green
        "#00FF00", // light green
        "#FFFF00", // yellow
        "#FFA500", // orange
        "#FF8C00", // dark orange
        "#FF4500", // red
        "#FF0000",  // bright red
    ];
    const TRAFFIC_THRESHOLDS = [1000, 2000, 3000, 4000, 5000, 6000, Infinity];

    const getTrafficColor = (volume) => {
        let idx = TRAFFIC_THRESHOLDS.findIndex(threshold => volume <= threshold);
        return TRAFFIC_COLORS[idx];
    };

    const RAMP_COLORS = {
        OR: '#2563eb',
        FR: '#9333ea',
    };

    const getTrafficPointColor = (traffic) => {
        const stationType = (traffic.station_type || 'ML').toString().trim().toUpperCase();
        if (stationType === 'OR' || stationType === 'FR') {
            return hexToRgb(RAMP_COLORS[stationType] || '#64748b');
        }
        const truckVolume = Number(traffic.truck_volume ?? traffic.volume ?? 0);
        return hexToRgb(getTrafficColor(truckVolume));
    };

    // Process traffic data
    const trafficLayers = useMemo(() => {
        if (!pemsTrafficCheckbox || !Array.isArray(trafficData) || trafficData.length === 0) {
            return [];
        }

        logger.log('Processing traffic data:', {
            count: trafficData.length,
            sampleItem: trafficData[0]
        });

        const processedTrafficData = trafficData.map(traffic => {
            let lng, lat;

            // Handle both GeoJSON Point object and WKT string formats
            if (traffic.location && typeof traffic.location === 'object' && traffic.location.type === 'Point') {
                // GeoJSON Point format: { type: "Point", coordinates: [lng, lat] }
                if (Array.isArray(traffic.location.coordinates) && traffic.location.coordinates.length >= 2) {
                    [lng, lat] = traffic.location.coordinates;
                } else {
                    logger.warn('Invalid GeoJSON Point coordinates:', {
                        id: traffic.id,
                        location: traffic.location
                    });
                    return null;
                }
            } else if (traffic.location && typeof traffic.location === 'string') {
                // WKT string format: SRID=4326;POINT (-118.157276 33.912001)
                const locationMatch = traffic.location.match(/POINT\s*\(\s*([^)]+)\s*\)/);
                if (!locationMatch) {
                    logger.warn('Invalid WKT location format:', {
                        id: traffic.id,
                        location: traffic.location
                    });
                    return null;
                }
                [lng, lat] = locationMatch[1].split(/\s+/).map(Number);
            } else {
                logger.warn('Invalid traffic location format:', {
                    id: traffic.id,
                    location: traffic.location,
                    type: typeof traffic.location
                });
                return null;
            }

            if (isNaN(lat) || isNaN(lng)) {
                logger.warn('Invalid traffic coordinates:', { id: traffic.id, lng, lat });
                return null;
            }

            const rgbColor = getTrafficPointColor(traffic);

                return {
                    ...traffic,
                    position: [lng, lat],
                    color: rgbColor,
                    // Override location with WKT string format for compatibility with InfoWindow
                    location: `SRID=4326;POINT (${lng} ${lat})`
                };
        }).filter(Boolean);

        logger.log(`Processing ${processedTrafficData.length} traffic points`);

        return [
            new ScatterplotLayer({
                id: 'traffic-layer',
                data: processedTrafficData,
                pickable: true,
                opacity: 0.8,
                stroked: true,
                filled: true,
                radiusScale: 50,
                radiusMinPixels: 4,
                radiusMaxPixels: 12,
                lineWidthMinPixels: 1,
                getPosition: d => d.position,
                // getRadius: d => Math.max(4, Math.min(12, d.volume / 500)),
                getFillColor: d => d.color,
                getLineColor: [255, 255, 255],
                onClick: (info, event) => {
                    if (onTrafficClick && info.object) {
                        onTrafficClick(info.object, event);
                    }
                }
            })
        ];
    }, [pemsTrafficCheckbox, trafficData, onTrafficClick]);

    const UTILITY_COLORS = [
        "#EDF67D", 
        "#F896D8",
        "#CA7DF9",
        "#724CF9",
        "#564592",
        "#CC3F0C"
    ]

    const utilitiesLayers = useMemo(() => {
        if (!utilitiesCheckbox || !Array.isArray(utilitiesData) || utilitiesData.length === 0) {
            return [];
        }

        logger.log("Utilities data: ", utilitiesData);
        logger.log("Utilities data length: ", utilitiesData.length);
        logger.log("Utilities data sample: ", utilitiesData[0]);

        // Process utilities data - assuming GeoJSON format
        // For MultiPolygons, flatten into individual polygons
        const processedUtilities = utilitiesData.flatMap(utility => {
            const { geometry, ...properties } = utility;
            if (!geometry?.coordinates) return [];

            let polygons = [];
            if (geometry.type === 'Polygon') {
                // Single polygon - outer ring only
                polygons = [geometry.coordinates[0].map(([lng, lat]) => [lng, lat])];
            } else if (geometry.type === 'MultiPolygon') {
                // Multiple polygons - take outer ring of each
                polygons = geometry.coordinates.map(poly => 
                    poly[0].map(([lng, lat]) => [lng, lat])
                );
            }

            let utilityColor = hexToRgb(UTILITY_COLORS[utility.id % UTILITY_COLORS.length]);
            // Create a separate entry for each polygon
            // Spread all properties (Utility, Type, Phone, Address, URL, etc.)
            return polygons.map(polygon => ({
                ...properties,  // This now includes all root-level properties
                polygon,
                color: utilityColor
            }));
        }).filter(Boolean);

        logger.log("Processed utilities: ", processedUtilities.length);
        logger.log("Processed utilities sample:", processedUtilities[0]);
        logger.log("Utilities sample polygon:", processedUtilities[0]?.polygon);

        if (!processedUtilities.length) return [];

        return [
            new PolygonLayer({
                id: 'utilities-layer',
                data: processedUtilities,
                pickable: true,
                opacity: 0.3,
                stroked: true,
                filled: true,
                getPolygon: d => d.polygon,
                getFillColor: d => d.color,
                // getLineColor: [255, 140, 0, 255], // Solid orange border
                getLineWidth: 2,
                lineWidthMinPixels: 1,
                onClick: (info) => {
                    logger.log('Utility clicked:', info.object);
                    if (onUtilityClick && info.object) {
                        onUtilityClick(info.object, info.coordinate);
                    }
                }
            })
        ];
    }, [utilitiesCheckbox, utilitiesData, onUtilityClick]);

    // Create overlay with stable reference
    const overlayRef = useRef(null);

    // Create overlay only once when map is available
    React.useEffect(() => {
        logger.log("=== DECK.GL OVERLAY CREATION ===");
        logger.log("Map available:", !!map);
        logger.log("Map type:", typeof map);
        logger.log("Overlay already exists:", !!overlayRef.current);

        if (!map || overlayRef.current) {
            logger.log("Skipping overlay creation - map:", !!map, "overlay exists:", !!overlayRef.current);
            return;
        }

        try {
            logger.log("Creating new GoogleMapsOverlay");
            overlayRef.current = new GoogleMapsOverlay({
                // Performance settings
                glOptions: {
                    antialias: false, // Disable for better performance
                    alpha: true
                },
                // Add these options to prevent zoom breakdown
                interleaved: false,
                pickingRadius: 10
            });

            overlayRef.current.setMap(map);
            logger.log("GoogleMapsOverlay created and attached to map successfully");
        } catch (error) {
            logger.error("Error creating GoogleMapsOverlay:", error);
        }

        return () => {
            if (overlayRef.current) {
                logger.log("Cleaning up GoogleMapsOverlay");
                overlayRef.current.setMap(null);
                overlayRef.current = null;
            }
        };
    }, [map]);

    // Update layers when data changes with debouncing
    React.useEffect(() => {
        if (!overlayRef.current || !map) return;

        const timeoutId = setTimeout(() => {
            logger.log('=== DECK.GL LAYER UPDATE ===');
            logger.log('Layer counts:', {
                utilities: utilitiesLayers.length,
                scaqmd: scaqmdLayers.length,
                sra: sraLayers.length,
                disadvantaged: disadvantagedLayers.length,
                calEnviroScreen: calEnviroScreenLayers.length,
                warehouses: warehouseLayers.length,
                parcels: parcelLayers.length,
                // parcelMarkers: parcelMarkerLayers.length,
                circuitsCombined: circuitLayersCombined.length,
                circuitContours: circuitContourLayers.length,
                mhd: mhdLayers.length,
                truckStops: truckStopLayers.length,
                traffic: trafficLayers.length
            });
            
            logger.log('Parcel layers active:', parcelLayers.length > 0);
            logger.log('Circuit layers active:', circuitLayersCombined.length > 0);
            
            const allLayers = [
                ...utilitiesLayers,
                ...scaqmdLayers,
                ...sraLayers,
                ...disadvantagedLayers,
                ...calEnviroScreenLayers,
                ...warehouseLayers,
                ...circuitLayersCombined,
                ...circuitContourLayers,
                ...heatmapHexagonLayers,  // Truck parking heatmap
                ...mhdLayers,
                ...truckStopLayers,
                ...trafficLayers,
                ...parcelLayers,
                ...parcelPointsLayers,
                ...topParcelPillLayer,
                ...topParcelPriceLabels,
            ];

            logger.log("Deck.gl updating with", allLayers.length, "layers");
            logger.log("Layer IDs:", allLayers.map(l => l.id));

            overlayRef.current.setProps({
                layers: allLayers
            });

            logger.log("Deck.gl overlay updated with layers");
        }, 100); // 100ms debounce

        return () => clearTimeout(timeoutId);
    }, [
        scaqmdLayers,
        sraLayers,
        warehouseLayers,
        disadvantagedLayers,
        calEnviroScreenLayers,
        parcelLayers,
        parcelPointsLayers,
        topParcelPillLayer,
        circuitLayersCombined,
        circuitContourLayers,
        heatmapHexagonLayers,
        mhdLayers,
        truckStopLayers,
        trafficLayers,
        utilitiesLayers
    ]);

    return null; // This component doesn't render anything directly
};

// Convert hex color to RGB array
const hexToRgb = (hex, alpha = 255) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
        alpha
    ] : [0, 0, 0, alpha];
};

const normalizeColorChannel = (value) => {
    if (!Number.isFinite(value)) return 0;
    if (value <= 1 && value >= 0) {
        return Math.round(value * 255);
    }
    return Math.round(Math.min(255, Math.max(0, value)));
};

const parseColorValue = (value, alpha = 255) => {
    if (value === null || value === undefined) return null;

    if (Array.isArray(value) && value.length >= 3) {
        const [r, g, b, a] = value;
        return [
            normalizeColorChannel(r),
            normalizeColorChannel(g),
            normalizeColorChannel(b),
            a !== undefined ? normalizeColorChannel(a) : alpha
        ];
    }

    if (typeof value === 'string') {
        if (value.trim().startsWith('#')) {
            return hexToRgb(value.trim(), alpha);
        }
        const rgbaMatch = value.match(/rgba?\(([^)]+)\)/i);
        if (rgbaMatch) {
            const parts = rgbaMatch[1].split(',').map(part => Number(part.trim()));
            if (parts.length >= 3) {
                return [
                    normalizeColorChannel(parts[0]),
                    normalizeColorChannel(parts[1]),
                    normalizeColorChannel(parts[2]),
                    parts[3] !== undefined ? normalizeColorChannel(parts[3]) : alpha
                ];
            }
        }
    }

    if (typeof value === 'object') {
        const { r, g, b, a } = value;
        if ([r, g, b].every(channel => channel !== undefined)) {
            return [
                normalizeColorChannel(r),
                normalizeColorChannel(g),
                normalizeColorChannel(b),
                a !== undefined ? normalizeColorChannel(a) : alpha
            ];
        }
    }

    if (typeof value === 'number') {
        const channel = normalizeColorChannel(value);
        return [channel, channel, channel, alpha];
    }

    return null;
};

const getColorFromContourProperties = (properties = {}, keys = [], fallback = [0, 0, 0, 255]) => {
    for (const key of keys) {
        if (!(key in (properties || {}))) continue;
        const parsed = parseColorValue(properties[key]);
        if (parsed) return parsed;
    }
    return fallback;
};

const LAND_VALUE_KEYS = [
    'land_value',
    'Land_Value',
    'landvalue',
    'LandValue',
    'avg_land_value',
    'Average_Land_Value',
    'average_land_value',
    'cluster_land_value',
    'Cluster_Land_Value'
];

const normalizeNumericValue = (value) => {
    if (value === undefined || value === null || value === '') return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    const stringValue = String(value).trim();
    if (!stringValue) return null;

    const cleaned = stringValue.replace(/[^0-9.\-]/g, '');
    if (!cleaned) return null;

    const numericValue = Number(cleaned);
    return Number.isNaN(numericValue) ? null : numericValue;
};

const getParcelLandValue = (properties = {}) => {
    for (const key of LAND_VALUE_KEYS) {
        const value = properties[key];
        const numericValue = normalizeNumericValue(value);
        if (numericValue !== null) {
            return numericValue;
        }
    }
    return null;
};

const getParcelLandValueLabel = (properties = {}) => {
    const landValue = getParcelLandValue(properties);
    return landValue !== null ? formatParcelDisplayValue(landValue) : '';
};

// Helper functions for parcel point markers
const formatParcelDisplayValue = (value) => {
    if (!Number.isFinite(value)) return '$0';
    if (value >= 1_000_000) {
        return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
        return `$${Math.round(value / 1_000)}K`;
    }
    return `$${Math.round(value)}`;
};

const buildParcelIconSvg = (text, { isCluster, highlighted }) => {
    const padding = 16;
    const estimatedWidth = Math.ceil(text.length * 8) + padding * 2;
    const width = Math.max(isCluster ? 60 : 56, estimatedWidth);
    const height = 30;
    const radius = height / 2;
    // Purple pill with white text; slightly lighter when highlighted
    const fill = highlighted ? '#A855F7' : '#7E3AF2';
    const stroke = highlighted ? '#C084FC' : '#5B21B6';
    const textColor = '#FFFFFF';

    const svg = `
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="${radius}" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
            <text x="${width / 2}" y="${height / 2 + 4}" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${text}</text>
        </svg>
    `;

    return {
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
        width,
        height,
        anchorX: width / 2,
        anchorY: height / 2
    };
};

const getParcelIconTexture = (cacheRef, text, options = {}) => {
    if (!cacheRef?.current) return null;
    const key = `${options.isCluster ? 'cluster' : 'parcel'}-${text}-${options.highlighted ? 'highlighted' : 'default'}`;
    if (!cacheRef.current.has(key)) {
        cacheRef.current.set(key, buildParcelIconSvg(text, options));
    }
    return cacheRef.current.get(key);
};

export default DeckGLOverlay;
